import { useFirestoreOrderBy, useFirestore } from "../../../firebase/useFirestore.js";
import CompagnyMeta from '../../../components/portfolio/CompagnyMeta.jsx';
import { client } from "../../../hooks/Client.js";
import { db, timestamp } from "../../../firebase/config.js";
import uuid from 'react-uuid';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import useSettings from "../../../hooks/Settings.jsx";
import { Auth } from '../../../StateManagment/Auth.js';
import { useContext, useState, useEffect, useRef, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { SavedIcon } from "../../../StateManagment/SavedIcon.js";
import PageHeader from "../../../components/layout/PageHeader.jsx";
import CopyLink from "../../../components/common/CopyLink"
import SelectPersonas from "../../../components/personas/SelectPersonas.jsx";
import EventOutputs from "../../../components/outputs/EventOutputs.jsx";
import OutputMeta from "../../../components/outputs/OutputMeta.jsx";
import QrCodeOutlinedIcon from '@mui/icons-material/QrCodeOutlined';
import DragIndicatorOutlinedIcon from '@mui/icons-material/DragIndicatorOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import AddBoxOutlinedIcon from '@mui/icons-material/AddBoxOutlined';
import EventResultsCounter from "../../../components/events/EventResultsCounter.jsx";
import ActivityMeta from "../../../components/activities/ActivityMeta.jsx";
import SuperAdmin from '../../../hooks/SuperAdmin';
import TasksModal from "../../../components/events/TasksModal.jsx";
import ActivityTasksCounter from "../../../components/events/ActivityTasksCounter.jsx";
import Table from "../../../components/common/table/Table.jsx";
import TableCell from "../../../components/common/table/TableCell.jsx";
import TableTitleCell from "../../../components/common/table/TableTitleCell.jsx";
import ReadOnlyGuard from "../../../components/common/ReadOnlyGuard";
import softDeleteDoc from '../../../helpers/softDeleteDoc';
import { DATE_FIELD_TITLE, OUTPUTS_FIELD_TITLE } from '../../../helpers/events/defaultEventFieldTitles';

const OutputResults = () => {
  // Context
  const [saved, setSaved] = useContext(SavedIcon);
  const [auth] = useContext(Auth);

  // State
  const [activityID, setActivityID] = useState('123');
  const [dragArrayResults, setDragArrayResults] = useState([]);
  const [dragArrayEvents, setDragArrayEvents] = useState([]);
  const [selectedSuperAdminActivity, setSelectedSuperAdminActivity] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedActivityFormId, setSelectedActivityFormId] = useState('');

  // ✅ Selection as Map: ID -> payload
  const [selectedMap, setSelectedMap] = useState(new Map());
  const selectAllRef = useRef(null);

  // Hooks
  const { personasOn: persona, portfolio } = useSettings();
  const history = useHistory();
  const superAdmin = SuperAdmin().admin;

  // Firestore
  const activities = useFirestoreOrderBy('Activities', 'Position', 'asc');
  const results = useFirestoreOrderBy('Results', 'Timestamp', 'asc');
  const events = useFirestoreOrderBy('EventForms', 'Position', 'asc');
  const oldEvents = useFirestore('Events');
  const notDeletedEvents = oldEvents && oldEvents.filter(event => !event.Deleted);
  const outputs = useFirestore('Outputs');
  const eventFields = useFirestore('EventFields');

  // ---- Memoized filtered results to avoid referential churn ----
  const notDeletedResults = useMemo(
    () => (results ? results.filter(r => !r.Deleted) : []),
    [results]
  );

  // ---- Activities that don't have a form yet ----
  const activitiesWithoutForm = useMemo(() => {
    const withForm = new Set((events || []).map(e => e.ActivityID));
    return (activities || []).filter(a => !withForm.has(a.ID));
  }, [activities, events]);

  const getActivityTitle = (activityId) =>
    activities?.find((activity) => activity.ID === activityId)?.Activity || '';

  const getOutputTitle = (outputId) =>
    outputs?.find((output) => output.ID === outputId)?.Title || '';

  // If selection becomes invalid (e.g., last missing item got a form), clear it
  useEffect(() => {
    if (!selectedSuperAdminActivity) return;
    const stillValid = activitiesWithoutForm.some(a => a.ID === selectedSuperAdminActivity);
    if (!stillValid) setSelectedSuperAdminActivity(null);
  }, [activitiesWithoutForm, selectedSuperAdminActivity]);

  // Keep DnD arrays in sync with live data
  useEffect(() => {
    setDragArrayEvents(events || []);
  }, [events]);

  useEffect(() => {
    setDragArrayResults(notDeletedResults || []);
  }, [notDeletedResults]);

  // DRAG & DROP: reorder Results and update Firestore
  const handleDragEndResults = (result) => {
    if (!result.destination) return;

    const items = Array.from(dragArrayResults);
    const [movedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, movedItem);

    setDragArrayResults(items);

    items.forEach((item, idx) => {
      db.collection('Results')
        .doc(item.docid)
        .update({ Position: idx + 1 });
    });

    setSaved('flex');
  };

  // DRAG & DROP: reorder Events and update Firestore
  const handleDragEndResultsEvents = (result) => {
    if (!result.destination) return;

    const items = Array.from(dragArrayEvents);
    const [movedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, movedItem);

    setDragArrayEvents(items);

    items.forEach((item, idx) => {
      db.collection('EventForms')
        .doc(item.docid)
        .update({ Position: idx + 1 });
    });

    setSaved('flex');
  };

  // Select activity
  const selectActivity = (e) => {
    const activityID = e.target.dataset.id;
    setActivityID(activityID);
  };

  // Set default activity
  useEffect(() => {
    activities.length > 0 ? setActivityID(activities[0].ID) : setActivityID(null);
  }, [activities]);

  // Pair old events
  const pairOldEventsHandler = async (e) => {
    const oldEventId = e.currentTarget.dataset.eventid;
    const activityId = e.currentTarget.dataset.activityid;
    const newEventFormId = events && events.find(event => event.ActivityID === activityId);

    // Query the new event fields id's
    let eventFieldOutputId = '';
    let eventFieldParticipantId = '';
    let eventFieldDateId = '';
    let eventFieldOpenId = '';

    // Get old event
    const oldEventSnapshot = await db.collection('Events')
      .where('CompagnyID', '==', client)
      .where('ID', '==', oldEventId)
      .get();

    const eventFieldSnapshot = await db.collection('EventFields')
      .where('EventId', '==', newEventFormId.ID)
      .get();

    eventFieldSnapshot.forEach((doc) => {
      const data = doc.data();
      if (data.Type === 'outputs') eventFieldOutputId = data.ID;
      if (data.Type === 'participants') eventFieldParticipantId = data.ID;
      if (data.Type === 'date') eventFieldDateId = data.ID;
      if (data.Type === 'open') eventFieldOpenId = data.ID;
    });

    const querySnapshot = await db.collection('Events')
      .where('CompagnyID', '==', client)
      .where('ID', '==', oldEventId)
      .get();

    querySnapshot.forEach(async (doc) => {
      const outputs = doc.data().Outputs;
      const participants = doc.data().Personas;
      const sessionId = uuid();
      const eventTimestamp = doc.data().Timestamp;
      const date = doc.data().Date;
      const oldDescription = doc.data().Description;

      // Date
      await db.collection('EventResults').doc().set({
        CompagnyID: client,
        Compagny: client,
        Timestamp: date || timestamp,
        EventID: newEventFormId.ID,
        SessionID: sessionId,
        User: auth.ID,
        FieldID: eventFieldDateId,
        Value: date,
        OptionID: null,
        Type: 'date',
        ID: uuid(),
        CreatedAt: timestamp
      });

      // Outputs
      if (outputs.length > 0) {
        for (const output of outputs) {
          await db.collection('EventResults').doc().set({
            Compagny: client,
            CompagnyID: client,
            ID: output.id,
            Value: Number(output.value || 0),
            Date: date,
            OutputID: output.id,
            ActivityID: activityId,
            EventID: newEventFormId.ID,
            SessionID: sessionId,
            User: auth.ID,
            FieldID: eventFieldOutputId,
            Timestamp: date,
            Type: 'outputs'
          });
        }
      }

      if (participants.length > 0) {
        for (const participant of participants) {
          await db.collection('EventResults').doc().set({
            CompagnyID: client,
            Compagny: client,
            Timestamp: eventTimestamp || timestamp,
            EventID: newEventFormId.ID,
            SessionID: sessionId,
            User: auth.ID,
            FieldID: eventFieldParticipantId,
            Value: participant,
            OptionID: null,
            Type: 'participants',
            ID: uuid(),
          });
        }
      }

      if (oldDescription) {
        await db.collection('EventResults').doc().set({
          CompagnyID: client,
          Compagny: client,
          Timestamp: eventTimestamp || timestamp,
          EventID: newEventFormId.ID,
          SessionID: sessionId,
          User: auth.ID,
          FieldID: eventFieldOpenId,
          Value: oldDescription,
          OptionID: null,
          Type: 'open',
          ID: uuid(),
        });
      }

      // set old event and outputs to deleted
      oldEventSnapshot.forEach(async (doc) => {
        const oldOutputs = doc.data().Outputs || [];

        // Set old outputs to deleted
        oldOutputs.forEach(async output => {
          await db.collection('Results')
            .where('CompagnyID', '==', client)
            .where('OutputID', '==', output.id)
            .where('Timestamp', '==', doc.data().Date)
            .get()
            .then(snapshot => {
              snapshot.forEach(doc => {
                db.collection('Results').doc(doc.id).update({ Deleted: true });
              });
            });
        });

        // Set old events to deleted
        await db.collection('Events').doc(doc.id).update({ Deleted: true });
      });
    });
  };

  // Change borderColor for non-existing outputs
  const borderColor = (outputId) => {
    const output = outputs && outputs.find(o => o.ID === outputId);
    return output ? 'green' : 'red';
  };

  // ===== Selection helpers (ID -> payload) =====

  // Build visible entries from what you actually render (dragArrayResults)
  const visibleEntries = useMemo(
    () => (dragArrayResults || []).map(r => ({
      id: r.ID,
      docid: r.docid,
      outputId: r.OutputID,
      activityId: r.ActivityID,
      timestamp: r.Timestamp,
      value: r.Result,
    })),
    [dragArrayResults]
  );

  const visibleIds = useMemo(
    () => visibleEntries.map(e => e.id),
    [visibleEntries]
  );

  const allSelected = visibleIds.length > 0 && visibleIds.every(id => selectedMap.has(id));
  const someSelected = visibleIds.some(id => selectedMap.has(id)) && !allSelected;

  useEffect(() => {
    if (selectAllRef.current) selectAllRef.current.indeterminate = someSelected;
  }, [someSelected]);

  // Prune selections if the visible list changes — guarded to avoid loops
  useEffect(() => {
    setSelectedMap(prev => {
      const keep = new Set(visibleIds);
      const next = new Map();
      prev.forEach((payload, id) => { if (keep.has(id)) next.set(id, payload); });

      if (next.size !== prev.size) return next;
      for (const [k, v] of next) {
        if (prev.get(k) !== v) return next;
      }
      return prev; // no change
    });
  }, [visibleIds]);

  const toggleOne = (id, docid, outputId, activityId, timestamp, value) => {
    setSelectedMap(prev => {
      const next = new Map(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.set(id, { docid, outputId, activityId, timestamp, value });
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedMap(prev => {
      const next = new Map(prev);
      if (allSelected) {
        visibleIds.forEach(id => next.delete(id));
      } else {
        visibleEntries.forEach(({ id, docid, outputId, activityId, timestamp, value }) => {
          next.set(id, { docid, outputId, activityId, timestamp, value });
        });
      }
      return next;
    });
  };

  // Pair old outputs handler (uses docid!)
  const pairOldOutputsHandler = async () => {
    const selectedPairs = Array.from(
      selectedMap,
      ([id, payload]) => ({ id, ...payload })
    );

    selectedPairs.forEach(async ({ id, docid, outputId, activityId, timestamp, value }) => {
      const sessionId = uuid();
      const eventForm = events && events.find(event => event.ActivityID === activityId);
      const eventFieldOutputId = eventFields && eventFields.find(field => field.EventId === eventForm.ID && field.Type === 'outputs')?.ID;

      // Create new EventResult
      await db.collection('EventResults').doc().set({
        Compagny: client,
        CompagnyID: client,
        ID: id,
        Value: Number(value || 0),
        Date: timestamp,
        OutputID: outputId,
        ActivityID: activityId,
        EventID: eventForm.ID,
        SessionID: sessionId,
        User: auth.ID,
        FieldID: eventFieldOutputId,
        Timestamp: timestamp,
        Type: 'outputs'
      });

      db.collection('Results').doc(docid).update({
        Deleted: true
      });
    });

    setSaved('flex');
    setSelectedMap(new Map()); // Clear selection
  };

  // Delete old results
  const deleteOldResults = async (e) => {
    const docid = e.currentTarget.dataset.docid;

    try {
      await softDeleteDoc({ collection: 'Results', docid });
    } catch (error) {
      console.error('Error deleting old results:', error);
    }
  };

  // Superadmin activity select handler
  const superadminSelectedActivity = (e) => {
    const activityId = e.target.options[e.target.selectedIndex].value;
    setSelectedSuperAdminActivity(activityId);
  };

  const openTasksModal = (activityFormId) => {
    setSelectedActivityFormId(activityFormId);
    setModalOpen(true);
  };

  // Superadmin create event form for activity (guarded to avoid duplicates)
  const createEventFormForActivity = async () => {
    if (!selectedSuperAdminActivity) return;

    // safety: prevent duplicates
    const alreadyHasForm = (events || []).some(e => e.ActivityID === selectedSuperAdminActivity);
    if (alreadyHasForm) return;

    const eventId = uuid();
    await db.collection('EventForms').add({
      Title: `Nieuw formulier `,
      Description: 'Vul hier de resultaten van de activiteit in',
      Date: timestamp,
      ActivityID: selectedSuperAdminActivity,
      ID: eventId,
      Compagny: client,
      CompagnyID: client,
      Version: '2',
      Position: (events?.length ?? 0) + 1,
      Timestamp: timestamp,
      User: auth.ID,
    });

    await db.collection('EventFields').add({
      EventId: eventId,
      Type: 'date',
      ID: uuid(),
      Compagny: client,
      CompagnyID: client,
      Timestamp: timestamp,
      Title: DATE_FIELD_TITLE,
      Position: 1
    });

    await db.collection('EventFields').add({
      EventId: eventId,
      Type: 'outputs',
      ID: uuid(),
      Compagny: client,
      CompagnyID: client,
      Timestamp: timestamp,
      Title: OUTPUTS_FIELD_TITLE,
      Position: 2
    });

    await db.collection('EventFields').add({
      EventId: eventId,
      Type: 'open',
      ID: uuid(),
      Compagny: client,
      CompagnyID: client,
      Timestamp: timestamp,
      Title: `Geef een beschrijving`,
      Position: 3
    });

    // Bewust geen deelnemersveld meer: deelnemers worden per output gekoppeld
    // ("Wie waren aanwezig?" onder het aantal), niet aan de activiteit als
    // geheel. Bestaande formulieren houden hun deelnemersveld — dat blijft
    // gewoon werken en zichtbaar — maar nieuwe formulieren krijgen het niet.
  };

  return (
    <div className="main">
      <div className="main-container">
        <PageHeader
          heading={'Activiteiten'}
          description={`Activiteiten zijn concrete acties die je onderneemt om je impact doel te bereiken. Hier registreer je de activiteiten die je hebt uitgevoerd aan de hand van formulieren.`}
          AI={false}
        />

        <ReadOnlyGuard edit="measurement.edit">
        {(canEdit) => (
        <div className='add-results-container'>

          <Table
            columns={[
              { key: 'drag', label: '', searchable: false },
              { key: 'activity', label: 'ACTIVITEIT' },
              { key: 'add', label: 'RESULTAAT TOEVOEGEN', searchable: false },
              { key: 'link', label: 'LINK', searchable: false },
              { key: 'qr', label: 'QR-CODE', searchable: false },
              { key: 'results', label: 'RESULTATEN', searchable: false },
              { key: 'tasks', label: 'PLANNEN', searchable: false },
            ]}
            items={dragArrayEvents || []}
            getItemKey={(item) => item.ID}
            renderRow={(item) => (
              <>
                <TableCell>
                  <DragIndicatorOutlinedIcon />
                </TableCell>
                <TableTitleCell
                    to={`/${client}/eventbuilder/${item.ID}`}
                    readOnly
                    displayContent={<ActivityMeta id={item.ActivityID} />}
                />
                <TableCell>
                  <AddBoxOutlinedIcon alt="" className="table-delete-icon" onClick={() => history.push(`/${client}/addevent/${item.ID}`)} />
                </TableCell>
                <TableCell>
                  <CopyLink link={`/${client}/eventopen/${item.ID}/open`} showOpen />
                </TableCell>
                <TableCell>
                  <QrCodeOutlinedIcon
                    className="table-delete-icon"
                    onClick={() => history.push(`/${client}/qrcode/${item.ID}/event`)}
                  />
                </TableCell>
                <TableCell onClick={() => history.push(`/${client}/eventresults/${item.ID}`)}>
                  <div className="event-form-results-column-container" >
                    <BarChartOutlinedIcon className="table-delete-icon" />
                    <div className="event-form-results-column-counter-container">
                      <EventResultsCounter eventId={item.ID} />
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  <ActivityTasksCounter activityFormId={item.ID} />
                  {canEdit && <EditOutlinedIcon onClick={() => openTasksModal(item.ID)} />}
                </TableCell>
              </>
            )}
            searchAccessor={(item) => getActivityTitle(item?.ActivityID)}
            searchPlaceholder="Zoeken op activiteit"
            emptyMessage="Geen activiteiten gevonden."
            draggable
            droppableId="events"
            onDragEnd={handleDragEndResultsEvents}
          />

          <TasksModal
            activityFormID={selectedActivityFormId}
            setModalOpen={setModalOpen}
            modalOpen={modalOpen}
            type='activityForm'
          />

          {/* Superadmin area to create a form for an activity — only show if some activities lack a form */}
          {activitiesWithoutForm.length > 0 && (
            <div className="table-container dashboard-container">
              <h2>Formulier aanmaken voor activiteit</h2>
              <p>Maak een formulier aan voor activiteiten zonder formulier</p>
              <select value={selectedSuperAdminActivity || ''} onChange={superadminSelectedActivity}>
                <option value="">Selecteer activiteit</option>
                {activitiesWithoutForm.map(activity => (
                  <option key={activity.ID} value={activity.ID}>{activity.Activity}</option>
                ))}
              </select>
              <button onClick={createEventFormForActivity} disabled={!selectedSuperAdminActivity}>Formulier aanmaken</button>
            </div>
          )}

          {/* Old events */}
          {(notDeletedEvents && notDeletedEvents.length > 0) && (
            <div className='table-outer-container'>
              <h2>Te koppelen oude gebeurtenissen</h2>
              <p>Hieronder staan de oude gebeurtenissen die nog gekoppeld moeten worden aan hun activiteit.</p>
              <Table
                columns={[
                  { key: 'drag', label: '', searchable: false },
                  { key: 'title', label: 'TITEL' },
                  { key: 'date', label: 'DATUM', searchable: false },
                  { key: 'outputs', label: 'OUTPUTS', searchable: false },
                  {
                    key: 'participants',
                    label: 'DEELNEMERS',
                    searchable: false,
                    headerContent: <span style={{ display: persona }}>DEELNEMERS</span>,
                  },
                  { key: 'pair', label: 'KOPPELEN', searchable: false },
                ]}
                items={notDeletedEvents || []}
                getItemKey={(item) => item.ID}
                renderRow={(item) => (
                  <>
                    <TableCell>
                      <DragIndicatorOutlinedIcon />
                    </TableCell>
                    <TableCell>
                      <p>{item.Title}</p>
                    </TableCell>
                    <TableCell>
                      <p>{item.Date.toDate().toISOString().split('T')[0]}</p>
                    </TableCell>
                    <TableCell>
                      <EventOutputs eventId={item.ID} />
                    </TableCell>
                    <TableCell style={{ display: persona }}>
                      <SelectPersonas event={item} />
                    </TableCell>
                    <TableCell>
                      <button data-eventid={item.ID} data-activityid={item.ActivityID} onClick={pairOldEventsHandler}>Koppelen</button>
                    </TableCell>
                  </>
                )}
                searchAccessor={(item) => item?.Title || ''}
                searchPlaceholder="Zoeken op titel"
                emptyMessage="Geen oude gebeurtenissen gevonden."
                draggable
                droppableId="events-old"
                onDragEnd={handleDragEndResultsEvents}
              />
            </div>
          )}

          {(notDeletedResults && notDeletedResults.length > 0) && (
            <div className='table-outer-container'>
              <h2>Losse outputs koppelen aan activiteit</h2>
              <p>Hieronder staan de losse outputs die nog gekoppeld moeten worden aan hun activiteit.</p>
              <Table
                columns={[
                  { key: 'drag', label: '', searchable: false },
                  {
                    key: 'select',
                    label: '',
                    searchable: false,
                    headerContent: (
                      <input
                        type="checkbox"
                        ref={selectAllRef}
                        checked={allSelected}
                        onChange={toggleSelectAll}
                        aria-checked={allSelected ? 'true' : (someSelected ? 'mixed' : 'false')}
                      />
                    ),
                  },
                  { key: 'output', label: 'OUTPUT' },
                  { key: 'result', label: 'RESULTAAT', searchable: false },
                  { key: 'date', label: 'DATUM', searchable: false },
                  {
                    key: 'project',
                    label: 'PROJECT',
                    searchable: false,
                    headerContent: <span style={{ display: portfolio }}>PROJECT</span>,
                  },
                  { key: 'delete', label: 'VERWIJDEREN', searchable: false },
                ]}
                items={dragArrayResults || []}
                getItemKey={(item) => item.ID}
                renderRow={(item) => (
                  <>
                    <TableCell>
                      <span className="drag-handle" onClick={(e) => e.stopPropagation()} title="Sleep om te verplaatsen">
                        <DragIndicatorOutlinedIcon />
                      </span>
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedMap.has(item.ID)}
                        onChange={() => toggleOne(item.ID, item.docid, item.OutputID, item.ActivityID, item.Timestamp, item.Result)}
                      />
                    </TableCell>
                    <TableCell style={{ border: `1px solid ${borderColor(item.OutputID)}` }}>
                      <OutputMeta id={item.OutputID} />
                    </TableCell>
                    <TableCell>
                      <p>{item.Result}</p>
                    </TableCell>
                    <TableCell>
                      <p>{item.Timestamp.toDate().toISOString().split('T')[0]}</p>
                    </TableCell>
                    <TableCell style={{ display: portfolio }}>
                      <CompagnyMeta id={item.ProjectId} />
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      {canEdit && (
                        <DeleteOutlinedIcon
                          className='table-delete-icon'
                          data-docid={item.docid}
                          onClick={deleteOldResults}
                        />
                      )}
                    </TableCell>
                  </>
                )}
                searchAccessor={(item) => getOutputTitle(item?.OutputID)}
                searchPlaceholder="Zoeken op output"
                emptyMessage="Geen losse outputs gevonden."
                draggable
                droppableId="results"
                onDragEnd={handleDragEndResults}
                getRowClassName={(item) => (selectedMap.has(item.ID) ? 'is-selected' : '')}
                rowProps={(item) => ({
                  onClick: () => toggleOne(item.ID, item.docid, item.OutputID, item.ActivityID, item.Timestamp, item.Result),
                })}
              />
              <div className="add-results-actions-container">
                <button onClick={pairOldOutputsHandler}>
                  Geselecteerde outputs koppelen
                </button>
              </div>
            </div>
          )}

        </div>
        )}
        </ReadOnlyGuard>
      </div>
    </div>
  );
};

export default OutputResults;
